import { useCallback, useRef } from 'react'

import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import {
  type BidWithdrawState,
  resolveSpotBidWithdrawState,
  withdrawStateHintKey,
} from '~/pages/SpotBidding/models/bidWithdrawState'
import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import { formatBlockBidLegSummary } from '~/pages/SpotBidding/models/blockBidWithdrawAction'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import { formatSpotBidWithdrawFailureSummary } from '~/pages/SpotBidding/models/spotBidWithdrawAction'

type ObservedWithdrawState = BidWithdrawState | 'none'

/** 取下の対象 (通常入札は札、 ブロック入札はグループ) を一覧の行から読む方法。 */
type WithdrawFailureNoticeSource<Row> = {
  titleKey: string
  /** 取下の対象の識別子。 同じ対象の行が複数ある時は先頭の行だけを見る。 */
  keyOf: (row: Row) => string
  stateOf: (row: Row) => BidWithdrawState | undefined
  summaryOf: (row: Row, t: TFunction) => string
}

const SPOT_BID_SOURCE: WithdrawFailureNoticeSource<SpotBidRow> = {
  titleKey: 'page:biddingSpot.withdrawFailedToast.title',
  keyOf: (row) => row.id,
  stateOf: (row) => resolveSpotBidWithdrawState(row.bidStatus),
  summaryOf: formatSpotBidWithdrawFailureSummary,
}

const BLOCK_BID_SOURCE: WithdrawFailureNoticeSource<BlockBidRow> = {
  titleKey: 'page:biddingBlock.withdrawFailedToast.title',
  // LINK / LOOP は 2 行で同じグループの値を持つので、 先頭 (親) 行で 1 回だけ見る。
  keyOf: (row) => row.groupId,
  stateOf: (row) => row.withdrawState,
  summaryOf: formatBlockBidLegSummary,
}

/** 一括取下 (AC-BSP-116) との関係で、 札ごとの通知を当てない対象。 */
export type BulkWithdrawRouting = {
  /**
   * 一括取下の応答を待っている札。 札ごとか一括かが決まっていないので、 知らせず、 観測も記録しない
   * (応答の後の観測で、 受け付けた札は一括の件数に、 受け付けなかった札は札ごとの通知に回る)。
   */
  isHeld: (key: string) => boolean
  /** 一括取下で受け付けて、 結果を件数のトーストで知らせる札。 知らせず、 観測だけを記録する。 */
  isBulkTracked: (key: string) => boolean
}

/** 一覧の 1 回の観測を札ごとの通知に当てる関数。 */
export type WithdrawFailureNotifier<Row> = (
  rows: readonly Row[],
  routing: BulkWithdrawRouting,
) => void

/**
 * 取下失敗の確定 (AC-BSP-057b) を、 開いている画面で観測した時に対象ごとに 1 回だけ
 * error トーストで知らせる。 通知手段は §エラーハンドリング「JEPX 送信失敗」 と同型
 * (エラートースト + 一覧の表示の変更)。
 *
 * 前回の観測が取下失敗でなかった対象が取下失敗になった時に出す。 初めて観測した
 * 対象 (画面を開く前に確定したもの・受渡日を切り替えた先のもの) は遷移を見ていない
 * ので出さない: その対象には一覧の「取下失敗」 の目印が残る。
 *
 * 観測はページの effect が一括取下の結果の観測より先に当てる (`useSpotWithdrawResultNotices`)。
 */
function useWithdrawFailureNotifier<Row>(
  source: WithdrawFailureNoticeSource<Row>,
  isBeforeDeadline: boolean,
): WithdrawFailureNotifier<Row> {
  const { t } = useTranslation()
  const showErrorToast = useErrorToast()
  const observations = useRef(new Map<string, ObservedWithdrawState>())

  return useCallback(
    (rows, { isHeld, isBulkTracked }) => {
      const seen = new Set<string>()
      for (const row of rows) {
        const key = source.keyOf(row)
        if (seen.has(key)) continue
        seen.add(key)
        if (isHeld(key)) continue
        const state = source.stateOf(row) ?? 'none'
        const previous = observations.current.get(key)
        observations.current.set(key, state)
        if (
          state !== 'withdrawFailed' ||
          previous === undefined ||
          previous === 'withdrawFailed' ||
          isBulkTracked(key)
        ) {
          continue
        }
        showErrorToast({
          title: t(source.titleKey),
          description: t('page:biddingSpot.withdrawFailedToast.description', {
            summary: source.summaryOf(row, t),
            hint: t(withdrawStateHintKey('withdrawFailed', !isBeforeDeadline)),
          }),
          duration: 7000,
        })
      }
    },
    [source, isBeforeDeadline, showErrorToast, t],
  )
}

/** 通常入札の札の取下失敗を知らせる ([[useWithdrawFailureNotifier]])。 */
export const useSpotBidWithdrawFailureNotifier = (
  isBeforeDeadline: boolean,
): WithdrawFailureNotifier<SpotBidRow> =>
  useWithdrawFailureNotifier(SPOT_BID_SOURCE, isBeforeDeadline)

/** ブロック入札グループの取下失敗を知らせる ([[useWithdrawFailureNotifier]])。 */
export const useBlockBidWithdrawFailureNotifier = (
  isBeforeDeadline: boolean,
): WithdrawFailureNotifier<BlockBidRow> =>
  useWithdrawFailureNotifier(BLOCK_BID_SOURCE, isBeforeDeadline)
